import { derived, writable } from "svelte/store";

export const dropdownStores = (priorityItemIds = []) => {
  // Convert to Map for O(1) lookup performance
  const priorityMap = new Map(priorityItemIds.map((id, index) => [id, index]));
  // An object of the form { id1: true, id2: true, ... } matching the state of selectedItems
  // Use itemTransform in utils.js to convert selectedItems to this format
  const chosen = writable([]);

  // Store to keep track of which items should be displayed at the top of the list
  // with a similar form to chosen above
  const topSet = writable([]);

  // Store to keep track of all items (needed incase items haven't been loaded yet)
  const allItems = writable([]);

  // Search query for filtering items
  const filterDropdownSearch = writable("");

  // Filter and sort items based on search query
  const filteredAndSortedItems = derived(
    [filterDropdownSearch, chosen, topSet, allItems],
    ([$filterDropdownSearch, $chosen, $topSet, $allItems]) => {
      // Reorder items with clear priority levels
      const sortItems = (arrayToSort) => {
        if ($allItems.length < 10) return arrayToSort; // Don't reorder small lists

        // Define sort rank: lower number = higher priority
        const getSortRank = (item) => {
          // Priority 1: Items in priorityMap (e.g., "No Campaign")
          if (priorityMap.has(item.id)) return priorityMap.get(item.id);

          // Priority 2: Selected items that were initially selected (1000+ to be after priority items)
          return $chosen[item.id] && $topSet[item.id] ? 1000 : 2000;
        };

        return arrayToSort.sort((a, b) => {
          const rankA = getSortRank(a);
          const rankB = getSortRank(b);

          // If ranks differ, sort by rank
          if (rankA !== rankB) return rankA - rankB;

          // Within same rank, maintain order for priority items or sort alphabetically
          return rankA < 1000 ? 0 : a.label.localeCompare(b.label);
        });
      };
      // Empty filter --> return all items
      if (!$filterDropdownSearch) return sortItems($allItems);

      // Filter items based on search query
      const value = $filterDropdownSearch.toLowerCase();
      return sortItems(
        $allItems.filter((item) => item.label.toLowerCase().includes(value)),
      );
    },
  );

  // Derived stores for reactive properties of the dropdown
  // Count how many truthy values there are in the chosen object
  const count = derived(
    chosen,
    ($chosen) => Object.values($chosen).filter(Boolean).length,
  );

  // If some items are selected, but not all, show a partial checkbox by 'Select All'
  const partial = derived(
    [count, allItems],
    ([$count, $allItems]) => $count > 0 && $count < $allItems.length,
  );

  // If all items are selected, show a full checkbox by 'Select All'
  // Also use this as an argument for the toggleAll function below
  const allSelected = derived(
    [count, allItems],
    ([$count, $allItems]) => $count === $allItems.length,
  );

  // Aim for the opposite of the current state (all selected or not all selected) using flipTo
  const toggleAll = (flipTo) => {
    chosen.update((c) =>
      Object.fromEntries(Object.keys(c).map((id) => [id, flipTo])),
    );

    // If all items are selected, clear the topSet as the ux is confusing
    topSet.update((c) =>
      Object.fromEntries(Object.keys(c).map((id) => [id, false])),
    );
  };

  // When clicking an option, update the chosen store (can update in bulk externally)
  const setItems = (items) => (selected) => {
    // Mark all items as selected or deselected (true or false)
    chosen.update((c) => ({
      ...c,
      ...Object.fromEntries(items.map((item) => [item.id, selected])),
    }));

    // If items are deselected, remove them from the topSet
    if (!selected) {
      topSet.update((c) => ({
        ...c,
        ...Object.fromEntries(items.map((item) => [item.id, false])),
      }));
    }
  };

  return {
    chosen,
    topSet,
    allItems,
    filterDropdownSearch,
    filteredAndSortedItems,
    count,
    partial,
    allSelected,
    toggleAll,
    setItems,
  };
};
